import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Search, Send, Loader2 } from "lucide-react";

const GREEN = "#16A34A";
const AMBER = "#F59E0B";
const RED = "#DC2626";

export const GapSuggestions = ({ canAssign }) => {
  const gsT = useTableSort([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState("");

  useEffect(() => {
    api.get("/dashboard/branch-contribution/suggestions")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Öneriler yüklenemedi"));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    return data.committees.filter((c) => !term
      || (c.name || "").toLocaleLowerCase("tr").includes(term)
      || String(c.committee_no) === term
      || c.items.some((i) => i.branch.toLocaleLowerCase("tr").includes(term)));
  }, [data, q]);

  const assign = async (cno, item) => {
    const key = `${cno}-${item.branch}`;
    setSending(key);
    try {
      const { data: res } = await api.post("/dashboard/branch-contribution/assign", {
        committee_no: cno, branch: item.branch, count: item.suggested_calls,
        note: "Açık kapatma önerisi üzerinden gönderildi",
      });
      toast.success(`Görev gönderildi: ${res.assigned.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Görev gönderilemedi");
    } finally {
      setSending("");
    }
  };

  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-4" data-testid="gap-suggestions">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { l: "Toplam Hedef Açığı", v: data.total_gap, c: RED },
          { l: "Havuzdan Kapatılabilir", v: data.total_coverable, c: GREEN },
          { l: "Açığı Olan Komite", v: data.committees.filter((c) => c.gap > 0).length, c: AMBER },
        ].map((k) => (
          <Card key={k.l} className="p-3" data-testid={`gs-kpi-${k.l}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
            <div className="text-xs text-muted-foreground">{k.l}</div>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Komite veya şube ara..." value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="gs-search" />
      </div>

      <div className="space-y-3">
        {rows.map((c) => (
          <Card key={c.committee_no} className="overflow-hidden" data-testid={`gs-committee-${c.committee_no}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
              <p className="font-semibold text-sm">{c.committee_no}. {c.name}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Hedef <b className="text-foreground">{c.target}</b></span>
                <span>Kesin Oy <b style={{ color: GREEN }}>{c.secured}</b></span>
                {c.gap > 0 ? (
                  <Badge variant="outline" style={{ color: RED, borderColor: RED }}>Açık {c.gap} oy</Badge>
                ) : (
                  <Badge variant="outline" style={{ color: GREEN, borderColor: GREEN }}>Hedefte</Badge>
                )}
                {c.uncoverable > 0 && (
                  <span style={{ color: RED }}>Havuz yetmiyor: {c.uncoverable} oy</span>
                )}
              </div>
            </div>
            {c.gap === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">Bu komite hedefte; ek arama önerisi yok.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr>
                      <gsT.Th sortKey="gs-sube" get={(r) => r.branch} text className="text-left font-medium py-2 px-3">Şube</gsT.Th>
                      <gsT.Th sortKey="gs-havuz" get={(r) => r.potential} className="text-right font-medium py-2 px-3">Kazanılabilir havuz</gsT.Th>
                      <gsT.Th sortKey="gs-arama" get={(r) => r.suggested_calls} className="text-right font-medium py-2 px-3">Aranacak firma</gsT.Th>
                      {canAssign && <th className="text-right font-medium py-2 px-3">Görev</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gsT.applySort(c.items).map((i) => (
                      <tr key={i.branch} className="border-b border-border/50 hover:bg-muted/30"
                        data-testid={`gs-row-${c.committee_no}-${i.branch}`}>
                        <td className="py-2 px-3 font-medium">{i.branch}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">{i.potential}</td>
                        <td className="py-2 px-3 text-right tabular-nums font-semibold" style={{ color: AMBER }}>
                          {i.suggested_calls}
                        </td>
                        {canAssign && (
                          <td className="py-2 px-3 text-right">
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              disabled={sending === `${c.committee_no}-${i.branch}`}
                              onClick={() => assign(c.committee_no, i)}
                              data-testid={`gs-assign-${c.committee_no}-${i.branch}`}>
                              {sending === `${c.committee_no}-${i.branch}`
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <><Send className="h-3.5 w-3.5 mr-1" /> Görev gönder</>}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}
        {rows.length === 0 && (
          <Card className="p-10 text-center text-sm text-muted-foreground">Kayıt bulunamadı</Card>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Açık = komite hedefi − kesin oy. Aranacak firma sayısı, şubelerin kazanılabilir havuzu
        (Belki / Ulaşılamadı / işaretsiz) oranında paylaştırılır; bir şubeden havuzundan fazlası istenmez.
        "Görev gönder", o şubenin şube yöneticilerine arama görevi ve sistem bildirimi oluşturur.
      </p>
    </div>
  );
};
